import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { BaseComponent } from '@macs-frontend/core';
import { State } from '@macs-frontend/reducers';
import { UserGeolocation } from '@macs-frontend/web-macs/core/services/places.service';
import { Store } from '@ngrx/store';
import { combineLatest, fromEvent, Observable } from 'rxjs';
import { debounceTime, map, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { PlacesService } from '../../../../core';
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import PlaceResult = google.maps.places.PlaceResult;
import PlaceDetailsRequest = google.maps.places.PlaceDetailsRequest;

interface LocationDropdownList {
  type: LocationDropdownListTypes;
  list: LocationDropdownListItem[];
}

interface LocationDropdownListItem {
  name: string;
  address: string;
  originalItem: AutocompletePrediction | PlaceResult;
}

enum LocationDropdownListTypes {
  Autocomplete,
  Near,
}

@Component({
  selector: 'md-location-dropdown',
  templateUrl: './location-dropdown.component.html',
  styleUrls: ['./location-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LocationDropdownComponent
  extends BaseComponent
  implements OnInit, OnDestroy
{
  @ViewChild('searchInput', { static: true })
  searchInput?: ElementRef<HTMLInputElement>;

  @Output()
  placeChange: EventEmitter<PlaceResult> = new EventEmitter<PlaceResult>();

  // @ts-expect-error
  foundPlaces$: Observable<LocationDropdownList>;

  constructor(
    private placesService: PlacesService,
    readonly cdr: ChangeDetectorRef,
    readonly store$: Store<State>,
  ) {
    super();
  }

  ngOnInit() {
    if (this.searchInput) {
      this.foundPlaces$ = combineLatest(
        fromEvent(this.searchInput.nativeElement, 'input').pipe(
          startWith({ target: this.searchInput.nativeElement }),
          map(e => (e.target as HTMLInputElement).value),
        ),
        this.placesService.locationChange$,
      ).pipe(
        takeUntil(this.destroy$),
        // @ts-expect-error
        debounceTime(400),
        switchMap(([value, geolocation]: [string, UserGeolocation]) =>
          value
            ? this.placesService.searchPlaces(value).pipe(
                map(
                  (places: AutocompletePrediction[]): LocationDropdownList => ({
                    type: LocationDropdownListTypes.Autocomplete,
                    list: places.map((place: AutocompletePrediction) => ({
                      name: place.structured_formatting.main_text,
                      address: place.structured_formatting.secondary_text,
                      originalItem: place,
                    })),
                  }),
                ),
              )
            : this.placesService.getNearPlaces().pipe(
                map(
                  (places: PlaceResult[]): LocationDropdownList => ({
                    type: LocationDropdownListTypes.Near,
                    // @ts-expect-error
                    list: places
                      .map((place: PlaceResult) => ({
                        name: place.name,
                        address: place.vicinity,
                        originalItem: place,
                      }))
                      .slice(0, 5),
                  }),
                ),
              ),
        ),
      );
    }
  }

  selectPlace(
    type: LocationDropdownListTypes,
    place: AutocompletePrediction | PlaceResult,
    request: Partial<PlaceDetailsRequest> = {},
  ) {
    if (type === LocationDropdownListTypes.Autocomplete) {
      this.placesService
        .getPlaceDetails(place as AutocompletePrediction, request)
        .pipe(takeUntil(this.destroy$))
        // @ts-expect-error
        .subscribe((details: PlaceResult) => {
          this.placeChange.emit(details);
        });
    } else if (type === LocationDropdownListTypes.Near) {
      this.placeChange.emit(place as PlaceResult);
    }
  }
}
